/**
 * Error boundary around the lazily-loaded 3D view.
 *
 * Without one, a failed chunk fetch takes down the WHOLE APP: React's
 * `lazy()` rethrows, nothing catches it, the tree unmounts and the
 * operator gets a blank page. Found by reloading straight into 3D while
 * the dev server was returning ERR_FAILED -- but a chunk fetch can fail
 * in production too, most obviously when a deploy lands mid-session.
 *
 * The brief is explicit that the view must never block the tab, so a
 * failure here falls back to 2D with a notice rather than to nothing.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** "load": the scene's code did not arrive (round 16 K15). */
  onError: (reason: "load") => void;
  fallback: ReactNode;
}

interface State {
  failed: boolean;
}

export class Twin3DBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.warn("[twin3d] failed to load, falling back to 2D", error, info);
    this.props.onError("load");
  }

  render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}
